const EVENT_KEYS = [
  "eventId",
  "guid",
  "laterality",
  "model",
  "occurredAt",
  "protocol",
  "status",
  "version",
];
const OUTCOMES = new Set(["applied", "ignored", "fallback"]);
export const DEFAULT_EVENT_ID_RETENTION_MS = 14 * 24 * 60 * 60 * 1000;
export const DEFAULT_INITIAL_SNAPSHOT_RETRY_DELAYS_MS = Object.freeze([
  250,
  1000,
]);

const isNonemptyString = (value) =>
  typeof value === "string" && value.trim().length > 0;

export const isValidMlStatusEvent = (event) => {
  if (!event || typeof event !== "object" || Array.isArray(event)) return false;
  if (Object.keys(event).sort().join("|") !== EVENT_KEYS.join("|")) return false;
  return (
    event.protocol === "telemed.ml-status.v1" &&
    Number.isSafeInteger(event.version) &&
    event.version >= 1 &&
    isNonemptyString(event.eventId) &&
    isNonemptyString(event.guid) &&
    isNonemptyString(event.model) &&
    (event.laterality === "left" || event.laterality === "right") &&
    isNonemptyString(event.status) &&
    typeof event.occurredAt === "string" &&
    !isNaN(Date.parse(event.occurredAt)) &&
    new Date(event.occurredAt).toISOString() === event.occurredAt
  );
};

const eventFingerprint = (event) =>
  EVENT_KEYS.map((key) => JSON.stringify(event[key])).join("|");

export const createMlStatusRepository = ({
  onError = () => {},
  now = Date.now,
  eventIdRetentionMs = DEFAULT_EVENT_ID_RETENTION_MS,
  initialSnapshotRetryDelaysMs = DEFAULT_INITIAL_SNAPSHOT_RETRY_DELAYS_MS,
  setTimer = (callback, delay) => setTimeout(callback, delay),
  clearTimer = (timer) => clearTimeout(timer),
} = {}) => {
  if (typeof now !== "function") throw new TypeError("now must be a function");
  if (!Number.isSafeInteger(eventIdRetentionMs) || eventIdRetentionMs < 1) {
    throw new TypeError("eventIdRetentionMs must be a positive safe integer");
  }
  if (typeof setTimer !== "function") {
    throw new TypeError("setTimer must be a function");
  }
  if (typeof clearTimer !== "function") {
    throw new TypeError("clearTimer must be a function");
  }
  const snapshotRetryDelays = Array.isArray(initialSnapshotRetryDelaysMs)
    ? initialSnapshotRetryDelaysMs.filter(
        (delay) => Number.isFinite(delay) && delay >= 0
      )
    : [];
  let active = true;
  let generation = 0;
  const subscriptions = new Set();
  const activeScopes = new Map();
  const seenEvents = new Map();
  const inFlightEvents = new Map();
  const resyncs = new Map();

  const report = (error) => {
    try {
      Promise.resolve(onError(error)).catch((innerError) => {
        console.warn("mlStatusRepository onError handler failed", innerError);
      });
    } catch (reportError) {
      console.warn("mlStatusRepository report failed", reportError);
    }
  };

  const trackScopeRequest = (
    subscription,
    run,
    onRejected,
    kind = "resync",
    eventId = null
  ) => {
    const existing = resyncs.get(subscription.scopeKey);
    if (existing) return existing.promise;
    let operation;
    try {
      operation = Promise.resolve(run());
    } catch (error) {
      operation = Promise.reject(error);
    }
    const entry = { eventId, kind, subscription, promise: null };
    const tracked = operation.finally(() => {
      if (resyncs.get(subscription.scopeKey) === entry) {
        resyncs.delete(subscription.scopeKey);
      }
    });
    entry.promise =
      typeof onRejected === "function" ? tracked.catch(onRejected) : tracked;
    resyncs.set(subscription.scopeKey, entry);
    return entry.promise;
  };

  const createContext = (subscription) => {
    const repositoryGeneration = generation;
    const subscriptionGeneration = subscription.generation;
    const controller = new AbortController();
    subscription.controllers.add(controller);
    const isCurrent = () =>
      active &&
      subscription.active &&
      generation === repositoryGeneration &&
      subscription.generation === subscriptionGeneration &&
      !controller.signal.aborted;
    return {
      signal: controller.signal,
      isCurrent,
      release: () => subscription.controllers.delete(controller),
    };
  };

  const runInitialSnapshot = (subscription, retryIndex = 0) => {
    if (typeof subscription.initialSnapshot !== "function") {
      return Promise.resolve(false);
    }
    let context;
    return trackScopeRequest(
      subscription,
      () => {
        context = createContext(subscription);
        return Promise.resolve()
          .then(() =>
            context.isCurrent()
              ? subscription.initialSnapshot(context)
              : undefined
          )
          .then((result) => {
            if (result === false || result?.outcome === "fallback") {
              throw new Error("initial ML status snapshot did not converge");
            }
            return result;
          })
          .finally(context.release);
      },
      (error) => {
        if (!context?.isCurrent()) return undefined;
        report(error);
        const delay = snapshotRetryDelays[retryIndex];
        if (delay === undefined) return undefined;
        let timer;
        timer = setTimer(() => {
          subscription.retryTimers.delete(timer);
          runInitialSnapshot(subscription, retryIndex + 1);
        }, delay);
        subscription.retryTimers.add(timer);
        return undefined;
      },
      "initial"
    );
  };

  const runResync = (subscription, reason, event) => {
    if (typeof subscription.resync !== "function") return Promise.resolve(false);
    const context = createContext(subscription);
    let result;
    try {
      result = subscription.resync({
        reason,
        ...(event ? { event } : {}),
        signal: context.signal,
        isCurrent: context.isCurrent,
      });
    } catch (error) {
      if (context.isCurrent()) report(error);
      context.release();
      return Promise.resolve(false);
    }
    return Promise.resolve(result)
      .then((result) => (context.isCurrent() ? result : false))
      .catch((error) => {
        if (context.isCurrent()) report(error);
        return false;
      })
      .finally(context.release);
  };

  const requestResync = (subscription, reason, event) => {
    return trackScopeRequest(subscription, () =>
      runResync(subscription, reason, event)
    );
  };

  const fallbackResync = async (subscription, event) => {
    while (active && subscription.active) {
      const existing = resyncs.get(subscription.scopeKey);
      if (!existing) {
        return trackScopeRequest(
          subscription,
          () => runResync(subscription, "fallback", event),
          undefined,
          "fallback",
          event.eventId
        );
      }
      if (
        existing.kind === "fallback" &&
        existing.eventId === event.eventId
      ) {
        return existing.promise;
      }
      try {
        await existing.promise;
      } catch (error) {
        // The post-event fallback must still run after older work settles.
      }
    }
    return false;
  };

  const project = async (subscription, event) => {
    if (typeof subscription.project !== "function") return "ignored";
    const context = createContext(subscription);
    try {
      const result = await subscription.project(event, {
        signal: context.signal,
        isCurrent: context.isCurrent,
      });
      if (!context.isCurrent()) return "ignored";
      return OUTCOMES.has(result?.outcome) ? result.outcome : "fallback";
    } catch (error) {
      return context.isCurrent() ? "fallback" : "ignored";
    } finally {
      context.release();
    }
  };

  const subscribe = ({
    scopeKey,
    resyncTargetKey,
    accepts,
    initialSnapshot,
    project: projectEvent,
    resync: resyncScope,
  } = {}) => {
    if (!active) return () => {};
    if (!isNonemptyString(scopeKey)) {
      throw new TypeError("scopeKey is required");
    }
    const subscription = {
      scopeKey,
      resyncTargetKey: isNonemptyString(resyncTargetKey)
        ? resyncTargetKey
        : null,
      accepts,
      initialSnapshot,
      project: projectEvent,
      resync: resyncScope,
      active: true,
      generation: 0,
      controllers: new Set(),
      retryTimers: new Set(),
    };
    subscriptions.add(subscription);
    const scopeCount = activeScopes.get(scopeKey) || 0;
    activeScopes.set(scopeKey, scopeCount + 1);
    if (scopeCount === 0) runInitialSnapshot(subscription);

    return () => {
      if (!subscription.active) return;
      subscription.active = false;
      subscription.generation += 1;
      subscription.controllers.forEach((controller) => controller.abort());
      subscription.controllers.clear();
      subscription.retryTimers.forEach((timer) => clearTimer(timer));
      subscription.retryTimers.clear();
      subscriptions.delete(subscription);
      const resync = resyncs.get(scopeKey);
      if (resync?.subscription === subscription) {
        resyncs.delete(scopeKey);
      }
      const remaining = (activeScopes.get(scopeKey) || 1) - 1;
      if (remaining > 0) activeScopes.set(scopeKey, remaining);
      else activeScopes.delete(scopeKey);
    };
  };

  const apply = async (event) => {
    if (!active) return "ignored";
    const applyGeneration = generation;
    if (!isValidMlStatusEvent(event)) {
      throw new Error("invalid ML status event");
    }
    const observedAt = now();
    if (!Number.isSafeInteger(observedAt) || observedAt < 0) {
      throw new Error("invalid repository clock");
    }
    seenEvents.forEach((known, eventId) => {
      if (known.expiresAt <= observedAt) seenEvents.delete(eventId);
    });
    const fingerprint = eventFingerprint(event);
    const knownEvent = seenEvents.get(event.eventId);
    if (knownEvent) {
      if (knownEvent.fingerprint !== fingerprint) {
        throw new Error("conflicting ML status event");
      }
      return "ignored";
    }
    const inFlight = inFlightEvents.get(event.eventId);
    if (inFlight) {
      if (inFlight.fingerprint !== fingerprint) {
        throw new Error("conflicting ML status event");
      }
      return inFlight.promise;
    }

    const applyEvent = (async () => {
      let fallbackFailed = false;
      const outcomes = await Promise.all(
        Array.from(subscriptions).map(async (subscription) => {
          if (!subscription.active) return "ignored";
          let accepted = true;
          try {
            accepted =
              typeof subscription.accepts !== "function" ||
              subscription.accepts(event);
          } catch (error) {
            report(error);
            accepted = false;
          }
          if (!accepted) return "ignored";
          const outcome = await project(subscription, event);
          if (outcome === "fallback") {
            const result = await fallbackResync(subscription, event);
            if (result === false || result?.outcome === "fallback") {
              fallbackFailed = true;
            }
          }
          return subscription.active ? outcome : "ignored";
        })
      );

      if (!active || generation !== applyGeneration) return "ignored";
      if (!fallbackFailed) {
        seenEvents.set(event.eventId, {
          fingerprint,
          expiresAt: Math.min(
            Number.MAX_SAFE_INTEGER,
            observedAt + eventIdRetentionMs
          ),
        });
      }
      if (outcomes.includes("fallback")) return "fallback";
      if (outcomes.includes("applied")) return "applied";
      return "ignored";
    })();
    inFlightEvents.set(event.eventId, { fingerprint, promise: applyEvent });
    return applyEvent.finally(() => {
      if (inFlightEvents.get(event.eventId)?.promise === applyEvent) {
        inFlightEvents.delete(event.eventId);
      }
    });
  };

  const resync = (reason = "reconnect") => {
    if (!active) return Promise.resolve([]);
    const scopes = new Map();
    subscriptions.forEach((subscription) => {
      if (
        subscription.active &&
        typeof subscription.resync === "function" &&
        !scopes.has(subscription.scopeKey)
      ) {
        scopes.set(subscription.scopeKey, subscription);
      }
    });
    return Promise.all(
      Array.from(scopes.values()).map((subscription) =>
        requestResync(subscription, reason)
      )
    );
  };

  const claimResyncTarget = (targetKey, reason = "manual") => {
    if (!active || !isNonemptyString(targetKey)) {
      return { disposition: "missing", promise: Promise.resolve(false) };
    }
    const matches = Array.from(subscriptions).filter(
      (subscription) =>
        subscription.active &&
        subscription.resyncTargetKey === targetKey &&
        typeof subscription.resync === "function"
    );
    if (matches.length === 0) {
      return { disposition: "missing", promise: Promise.resolve(false) };
    }
    if (matches.length > 1) {
      return { disposition: "ambiguous", promise: Promise.resolve(false) };
    }
    return {
      disposition: "claimed",
      promise: requestResync(matches[0], reason),
    };
  };

  const resyncTarget = (targetKey, reason = "manual") =>
    claimResyncTarget(targetKey, reason).promise;

  const cleanup = () => {
    if (!active) return;
    active = false;
    generation += 1;
    subscriptions.forEach((subscription) => {
      subscription.active = false;
      subscription.generation += 1;
      subscription.controllers.forEach((controller) => controller.abort());
      subscription.controllers.clear();
      subscription.retryTimers.forEach((timer) => clearTimer(timer));
      subscription.retryTimers.clear();
    });
    subscriptions.clear();
    activeScopes.clear();
    resyncs.clear();
    inFlightEvents.clear();
    seenEvents.clear();
  };

  return {
    subscribe,
    apply,
    resync,
    resyncTarget,
    claimResyncTarget,
    cleanup,
  };
};

export default createMlStatusRepository;
